import { Box, Flex, HStack, Stack, useMergeRefs, VStack } from '@chakra-ui/react';
import { SearchField } from '@main/complykit/components/search-field';
import {
  SegmentedControl,
  SegmentedControlItem,
} from '@main/complykit/components/segmented-control';
import { Text } from '@main/complykit/components/text';
import { DynamicFieldStateInput, QuestionnaireComponentKind } from '@main/dynamic-form';
import { DynamicForm, DynamicFormRef } from '@main/dynamic-form/react';
import { VendorQuestionnaireFormAnswerValue } from '@main/graphql/client-scalars';
import { useStableCallback } from '@main/shared/browser';
import { ForwardedRef, forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { UseFormProps, WatchObserver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { flattenFields } from '../utils';
import { QuestionnaireFormContextValue, QuestionnnaireFormContext } from './context';
import { QuestionnaireFieldCommentPrefix } from './field-comment';
import { QuestionnnaireFieldWrapper } from './field-wrapper';
import { useQuestionnaireComponentRegistry } from './fields';
import {
  QuestionnnaireFieldWrapperProps,
  QuestionnnaireFormFieldAnswer,
  QuestionnnaireFormFilterMode,
  QuestionnnaireFormFilters,
  QuestionnnaireFormMode,
  QuestionnnaireFormProps,
  QuestionnnaireFormValues,
} from './form-types';
import { useQuestionnaireErrorMessage } from './use-error-message';
import { useQuestionnaireFormStats } from './use-stats';
import { useVisibleFieldsEx } from './use-visible-fields';
import { createNewAnswerFor, getQuestionFilter } from './utils';

export const QuestionnnaireForm = forwardRef(
  (props: QuestionnnaireFormProps, forwardedRef: ForwardedRef<DynamicFormRef>) => {
    const { t } = useTranslation();
    const componentRegistry = useQuestionnaireComponentRegistry({
      isReadView: props.mode === QuestionnnaireFormMode.View,
    });
    const dynamicFormRef = useRef<DynamicFormRef>(null);
    const ref = useMergeRefs(dynamicFormRef, forwardedRef);

    const [filters, setFilters] = useState<QuestionnnaireFormFilters>({
      mode: QuestionnnaireFormFilterMode.All,
      search: '',
    });

    const flatFields = useMemo(() => flattenFields(props.fields), [props.fields]);
    const fieldsMap = useMemo(() => new Map(flatFields.map((f) => [f.name, f])), [flatFields]);
    const answersMap = useMemo(
      () => new Map(props.answers?.map((answer) => [answer.field_name, answer])),
      [props.answers],
    );
    const filtersFilter = useMemo(() => getQuestionFilter(filters), [filters]);
    const { getFilteredFieldsRecursive } = useVisibleFieldsEx({
      flatFields,
      fieldsMap,
      questionFields: [],
      answers: props.answers ?? [],
      answersMap,
      shouldSkipFilteringFields: props.shouldSkipFilteringFields,
      filtersFilter,
    });
    const visibleFields = useMemo(
      () => getFilteredFieldsRecursive(props.fields),
      [getFilteredFieldsRecursive, props.fields],
    );
    const questionFields = useMemo(
      () =>
        flattenFields(visibleFields).filter((f) => f.kind !== QuestionnaireComponentKind.Section),
      [visibleFields],
    );

    const context: QuestionnaireFormContextValue = useMemo(
      () => ({
        flatFields,
        fieldsMap,
        questionFields,
        answers: props.answers ?? [],
        answersMap,
        shouldSkipFilteringFields: props.shouldSkipFilteringFields,
        filtersFilter,
      }),
      [
        flatFields,
        fieldsMap,
        questionFields,
        props.answers,
        props.shouldSkipFilteringFields,
        answersMap,
        filtersFilter,
      ],
    );
    const stats = useQuestionnaireFormStats(props.fields, context);
    const isAllQuestionsHidden = visibleFields.length === 0;

    const values: QuestionnnaireFormValues = useMemo(
      () =>
        Object.fromEntries(
          props.answers?.flatMap((answer) => [
            [answer.field_name, answer.value],
            [`${QuestionnaireFieldCommentPrefix}${answer.field_name}`, answer.comment],
          ]) ?? [],
        ),
      [props.answers],
    );

    const useFormProps: UseFormProps = useMemo(
      () => ({ values, resetOptions: { keepDirtyValues: true, keepErrors: true } }),
      [values],
    );

    const fieldState: DynamicFieldStateInput = useMemo(
      () => ({ isReadOnly: props.mode === QuestionnnaireFormMode.View }),
      [props.mode],
    );
    const errorMessage = useQuestionnaireErrorMessage();

    const onAnswerChange = useStableCallback(props.onAnswerChange);
    const wrapperProps: QuestionnnaireFieldWrapperProps = useMemo(
      () => ({ form: props, filters, visibleFields, onAnswerChange }),
      [props, filters, visibleFields, onAnswerChange],
    );

    const onStatsChange = props.onStatsChange;
    useEffect(
      () => onStatsChange?.({ total: stats.total, completed: stats.completed }),
      [onStatsChange, stats.total, stats.completed],
    );

    const onFormChange = useStableCallback(
      (...[data, info]: Parameters<WatchObserver<Record<string, unknown>>>) => {
        if (!info.name || info.type !== 'change') {
          return;
        }
        const isCommentUpdate = info.name.startsWith(QuestionnaireFieldCommentPrefix);
        const fieldName = isCommentUpdate
          ? info.name.replace(QuestionnaireFieldCommentPrefix, '')
          : info.name;
        const field = fieldsMap.get(fieldName);
        if (!field) {
          return;
        }
        const answerData: Partial<QuestionnnaireFormFieldAnswer> = {
          value: data[fieldName] as VendorQuestionnaireFormAnswerValue | undefined,
          comment: data[`${QuestionnaireFieldCommentPrefix}${fieldName}`] as string | undefined,
        };
        const answer = answersMap.get(field.name) ?? createNewAnswerFor(field);
        void onAnswerChange(answer, answerData);
      },
    );

    useEffect(() => dynamicFormRef.current?.form.watch(onFormChange).unsubscribe, [onFormChange]);

    return (
      <QuestionnnaireFormContext.Provider value={context}>
        <Stack spacing={6}>
          {props.mode === QuestionnnaireFormMode.Answer && !props.isSubmitted && (
            <HStack justifyContent="space-between">
              <SegmentedControl
                label={t('questionnaires.form.filters.label')}
                value={filters.mode}
                onChange={(mode) =>
                  setFilters((f) => ({ ...f, mode: mode as QuestionnnaireFormFilterMode }))
                }
              >
                <SegmentedControlItem id={QuestionnnaireFormFilterMode.All}>
                  {t('questionnaires.form.filters.all')}
                </SegmentedControlItem>
                <SegmentedControlItem id={QuestionnnaireFormFilterMode.Unanswered}>
                  {t('questionnaires.form.filters.unanswered')}
                </SegmentedControlItem>
              </SegmentedControl>

              <Box w="full" maxW="xs">
                <SearchField
                  aria-label={t('questionnaires.form.filters.searchLabel')}
                  placeholder={t('questionnaires.form.filters.searchPlaceholder')}
                  clearLabel={t('questionnaires.form.filters.clearSearch')}
                  value={filters.search}
                  onChange={(value) => setFilters((f) => ({ ...f, search: value.toLowerCase() }))}
                />
              </Box>
            </HStack>
          )}
          <Flex flexDir="column" gap="4">
            {props.mode === QuestionnnaireFormMode.Answer && (
              <Text size="xs" color="muted">
                {t('questionnaires.form.requiredNote')}
              </Text>
            )}
            <DynamicForm
              {...componentRegistry}
              fieldSortDir="asc"
              useForm={useFormProps}
              fields={visibleFields}
              fieldState={fieldState}
              errorMessage={errorMessage}
              fieldWrapper={props.fieldWrapper ?? QuestionnnaireFieldWrapper}
              fieldWrapperProps={wrapperProps}
              form={{ style: { display: 'contents' } }}
              ref={ref}
            />
            {isAllQuestionsHidden && (
              <VStack>
                <Text size="lg" color="muted">
                  {t('questionnaires.form.noMatchingQuestions')}
                </Text>
              </VStack>
            )}
          </Flex>
        </Stack>
      </QuestionnnaireFormContext.Provider>
    );
  },
);
